import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Scale, Receipt, TrendingUp, Gavel, FileText, Building2, CreditCard, ArrowRight, Truck, Banknote, IdCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import lawBuildingImage from '@/assets/law-building.jpg';
import { useEffect, useState } from 'react';
import SEO from '@/components/SEO';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground mb-6 leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

// Animation component for sections appearing
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

const ProcessesPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Services & Processes | Freeman & Crown Law"
        description="Discover our constitutional law services including political status correction, private trust establishment, tax liability removal, and legal consultation services."
        image="/lovable-uploads/a53f5ba2-203a-4732-899c-ff2777cb7d2c.png"
        url={window.location.origin + '/services'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-4">
              Services
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Empowering the sovereign individual through lawful processes and constitutional remedies. We don't just defend rights—we enforce them with authority and precision.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="lg:mt-8">
                <h2 className="font-rockwell text-xl md:text-2xl font-semibold text-primary mb-6">
                  For We The People of the Union
                </h2>
                <AnimatedParagraph delay={200}>
                  Our lawful processes are designed to empower the sovereign individual—the man and woman of the Republic—to reclaim rightful standing and traverse the legal terrain with dignity, clarity, and Divine authority.
                </AnimatedParagraph>
                <AnimatedParagraph delay={600}>
                  Each remedy we offer is born of deep innerstanding—a harmonious fusion of wisdom, experience, and disciplined application of Natural Law and constitutional enforcement. We engage in the precise execution of lawful status correction, the establishment of private conveyances through irrevocable trusts and estates, and the full restoration of constitutional protections in every domain of life.
                </AnimatedParagraph>
                <AnimatedParagraph delay={1000}>
                  We hold that it is not merely advantageous—but essential—for all those who walk with us to possess the knowledge, awareness, and lawful tools required to maintain their standing and to protect their rights with intention, confidence, and command.
                </AnimatedParagraph>
                <AnimatedParagraph delay={1400}>
                  And make no mistake—we do not only defend our rights, we advance. We move with authority. We confront every violation with the force of lawful remedy.
                </AnimatedParagraph>
                <AnimatedParagraph delay={1800}>
                  We stand not as passive observers, but as active executors of justice—ensuring that no breach goes unanswered, and no infringement is allowed to take root.
                </AnimatedParagraph>
                <AnimatedParagraph delay={2200}>
                  Our processes are constructed upon the eternal foundation of Natural Law, providing our members with the clarity, strength, and unshakable footing needed to not only assert—but to enforce—their rights in full.
                </AnimatedParagraph>
              </div>
            </div>

            <div className="mt-12 core-services-section">
              <div className="mb-12">
                <div className="space-y-6 mb-16">
                  <AnimatedSection delay={400}>
                    <Link to="/services/discharge-loan" className="p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 hover:border-gold/30 transition-all duration-300 ease-out hover:shadow-lg hover:shadow-gold/20 cursor-pointer block group">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center">
                          <Banknote className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">Discharge Mortgages, Car Loans & Credit Cards</h3>
                            <p className="text-muted-foreground leading-relaxed">
                              Legal assistance to discharge your mortgage, car loan, or credit card debt through proper lawful procedure.
                            </p>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                  <AnimatedSection delay={800}>
                    <Link to="/services/private-trust" className="p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 hover:border-gold/30 transition-all duration-300 ease-out hover:shadow-lg hover:shadow-gold/20 cursor-pointer block group">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center">
                          <Receipt className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">Trust Formation / Estate Planning</h3>
                            <p className="text-muted-foreground leading-relaxed">
                              Professional estate plan via private trust with full documentation in 10 days.
                            </p>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                  <AnimatedSection delay={1200}>
                    <Link to="/services/tax-liability" className="p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 hover:border-gold/30 transition-all duration-300 ease-out hover:shadow-lg hover:shadow-gold/20 cursor-pointer block group">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center">
                          <TrendingUp className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">Zero any IRS Federal Tax Obligation</h3>
                            <p className="text-muted-foreground leading-relaxed">
                              Assistance to eliminate IRS tax obligations through proper lawful procedure
                            </p>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                <AnimatedSection delay={2000}>
                    <Link to="/services/tax-id" className="p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 hover:border-gold/30 transition-all duration-300 ease-out hover:shadow-lg hover:shadow-gold/20 cursor-pointer block group">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center">
                          <IdCard className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">Obtain a U.S. Tax ID Number</h3>
                            <p className="text-muted-foreground leading-relaxed">
                              Assistance to obtain your U.S. Tax ID Number (ITIN) for people foreign to the U.S.
                            </p>
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                </div>

                <AnimatedSection delay={1600}>
                  <Link to="/education" className="block">
                    <div className="p-8 bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20">
                      <div className="mb-4">
                        <div className="w-12 h-1 bg-primary mb-6"></div>
                      </div>
                      <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                        Educate Yourself So You Can Stand On Your Own Two Feet
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-6 text-sm">
                        All our resources and processes are completely free. Empower yourself to stand on your own without dependency on anyone or anything.
                      </p>
                      <div className="flex justify-start">
                        <Button 
                          variant="link"
                          className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 pointer-events-none"
                        >
                          Go to Free Resources <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      
      <Footer />
    </div>
  );
};

export default ProcessesPage;